import { useState, useCallback, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { googleAuth } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Heart, Loader2, AlertCircle } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [isGoogleLoggingIn, setIsGoogleLoggingIn] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [scriptLoadFailed, setScriptLoadFailed] = useState(false);
  const oauthClientRef = useRef(null);

  const routeAfterAuth = useCallback(
    (u) => {
      if (!u) {
        navigate("/", { replace: true });
        return;
      }
      if (u.legal_acceptance?.required) {
        navigate("/accept-legal", { replace: true });
      } else if (u.onboarding_complete === false) {
        navigate("/profile/setup", { replace: true });
      } else {
        navigate("/find", { replace: true });
      }
    },
    [navigate],
  );

  const handleCredentialResponse = useCallback(
    async (response) => {
      try {
        if (!response.credential) throw new Error("No credential received");
        const payload = JSON.parse(atob(response.credential.split(".")[1]));
        const refCode = localStorage.getItem("stokvel_ref");
        await googleAuth(
          response.credential,
          payload.email,
          payload.name,
          payload.picture,
          refCode,
        );
        const refreshed = await refresh();
        toast.success("Welcome back!", {
          description: "You've successfully signed in.",
        });
        routeAfterAuth(refreshed);
      } catch (error) {
        console.error("Google login failed:", error);
        toast.error("Login Failed", {
          description: error.message || "Unable to sign in with Google.",
        });
      } finally {
        setIsGoogleLoggingIn(false);
      }
    },
    [refresh, routeAfterAuth],
  );

  const handleOAuthCallback = useCallback(
    async (tokenResponse) => {
      if (tokenResponse.error) {
        console.error("OAuth fallback error:", tokenResponse);
        toast.error("Login Failed", {
          description: "Unable to sign in. Please try again.",
        });
        setIsGoogleLoggingIn(false);
        return;
      }
      try {
        const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch user info");
        const profile = await res.json();
        await googleAuth(
          tokenResponse.access_token,
          profile.email,
          profile.name,
          profile.picture,
          localStorage.getItem("stokvel_ref"),
        );
        const refreshed = await refresh();
        toast.success("Welcome back!", {
          description: "You've successfully signed in.",
        });
        routeAfterAuth(refreshed);
      } catch (error) {
        console.error("OAuth fallback login failed:", error);
        toast.error("Login Failed", {
          description: error.message || "Unable to sign in. Please try again.",
        });
      } finally {
        setIsGoogleLoggingIn(false);
      }
    },
    [refresh, routeAfterAuth],
  );

  const handleOAuthFallback = useCallback(() => {
    if (oauthClientRef.current) {
      oauthClientRef.current.requestAccessToken();
    } else {
      setIsGoogleLoggingIn(false);
      setScriptLoadFailed(true);
      toast.error("Login unavailable", {
        description:
          "Google sign-in is blocked. Please disable content blockers and reload, or use the app instead.",
      });
    }
  }, []);

  const handleGoogleLogin = useCallback(() => {
    if (isGoogleLoggingIn || !googleReady) return;
    setIsGoogleLoggingIn(true);

    if (!window.google?.accounts?.id) {
      handleOAuthFallback();
      return;
    }

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        handleOAuthFallback();
      } else if (notification.isDismissedMoment()) {
        setIsGoogleLoggingIn(false);
      }
    });
  }, [isGoogleLoggingIn, googleReady, handleOAuthFallback]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn("Google Client ID not configured");
      setScriptLoadFailed(true);
      return;
    }

    const initGoogle = () => {
      if (!window.google?.accounts?.id) {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = initGoogle;
        script.onerror = () => {
          console.error("Google GSI script failed to load");
          setScriptLoadFailed(true);
          toast.error("Google sign-in unavailable", {
            description:
              "Please check your connection, disable content blockers, and reload.",
          });
        };
        document.head.appendChild(script);
        return;
      }

      if (window.__googleInitialized) {
        setGoogleReady(true);
        if (
          !oauthClientRef.current &&
          window.google?.accounts?.oauth2 &&
          clientId
        ) {
          oauthClientRef.current = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: "openid email profile",
            callback: handleOAuthCallback,
          });
        }
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        ux_mode: "popup",
        use_fedcm_for_prompt: false,
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: (response) => handleCredentialResponse(response),
      });

      if (window.google?.accounts?.oauth2 && clientId) {
        oauthClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: "openid email profile",
          callback: handleOAuthCallback,
        });
      }

      window.__googleInitialized = true;
      setGoogleReady(true);
    };

    initGoogle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <div className="w-12 h-12 bg-[#0F0F10] text-white rounded-xl flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6" fill="currentColor" />
            </div>
          </Link>
          <h1 className="font-display text-2xl font-semibold">Welcome to Haven</h1>
          <p className="text-sm text-[#6B6B70] mt-1">Sign in to continue</p>
        </div>

        <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 space-y-5">
          {scriptLoadFailed && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm text-red-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Google sign-in unavailable</p>
                <p className="text-xs mt-0.5">
                  Disable content blockers, then{" "}
                  <button
                    onClick={() => window.location.reload()}
                    className="underline font-medium hover:text-red-900"
                  >
                    reload the page
                  </button>
                  . Or install Haven on Google Play.
                </p>
              </div>
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            disabled={isGoogleLoggingIn || !googleReady || scriptLoadFailed}
            className="neo-btn bg-white border-gray-300 text-[#0F0F10] !px-4 !h-12 !text-base !rounded-full shadow-sm hover:bg-gray-50 disabled:opacity-50 w-full flex items-center justify-center gap-2"
          >
            {isGoogleLoggingIn ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <img
                src="https://www.google.com/favicon.ico"
                alt=""
                className="w-5 h-5"
              />
            )}
            {isGoogleLoggingIn ? "Signing in..." : "Continue with Google"}
          </button>

          <p className="text-xs text-center text-[#6B6B70] leading-relaxed">
            Email sign-in is currently unavailable. Please use Google.
          </p>

          <div className="pt-4 border-t border-[#E7E5E0]">
            <p className="text-xs text-center text-[#6B6B70] leading-relaxed">
              By continuing you agree to our{" "}
              <Link to="/terms" className="text-purple-600 underline hover:text-purple-700">
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-purple-600 underline hover:text-purple-700">
                Privacy Policy
              </Link>
              .
            </p>
            <p className="text-xs text-center text-[#6B6B70] leading-relaxed mt-2">
              Haven is strictly for adults <strong>18 and over</strong>.
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-[#6B6B70] hover:text-[#0F0F10]">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}